import { GoAAccordion, GoACallout, GoASpacer } from '@abgov/react-components';
import { Collision, Passenger } from '../../models/collision';
import {
  InitialPointOfImpact,
  LightConditionA,
  ObjectIdentification,
  ConfigurationState,
  isHitAndRun,
} from '../../state/slices/configurationSlice';
import { MediaState } from '../../state/slices/mediaSlice';
import { ImageCard, loadImage } from '../media/image-card-component';
import useCsrService from '../../services/csr-api-redux';
import { useIsDesktop } from '../../utils/isDesktop';

import styles from './review-component.module.css';

import { ReviewMapComponent } from '../location/location-review-component';
import { REACT_APP_GOOGLE_API_KEY } from '../../state/configuration';
import { APIProvider } from '@vis.gl/react-google-maps';
import moment from 'moment';
import { useEffect, useState } from 'react';

import {
  getConfigurationByDescription,
  CollisionTypes,
} from '../../state/slices/configurationSlice';
import { AuthenticationState } from '../../state/slices/authSlice';
import { FormStep } from '../../models/collision';
import {
  displaySection,
  getYesNoOrUnknownString,
} from '../../common/ui-library';

export interface CopyGeneratorProps {
  collision: Collision;
  stateConfiguration: ConfigurationState;
  printView?: boolean;
}

export const ReportStatusCopyGenerator = ({
  collision,
  stateConfiguration,
  stateAuth,
}: {
  collision: Collision;
  stateConfiguration: ConfigurationState;
  stateAuth: AuthenticationState;
}) => {
  const getStatusCallout = (steps: FormStep[] | undefined): JSX.Element => {
    if (steps) {
      let foundSteps: FormStep[];
      if (isHitAndRun(collision.collisionType!, stateConfiguration)) {
        foundSteps = steps.filter(
          (step) => step.step !== 7 && step.step !== 9 && step.step !== 10,
        );
      } else {
        foundSteps = foundSteps = steps.filter((step) => step.step !== 10);
      }

      if (foundSteps.find((step) => step.completed === false) == undefined) {
        return (
          <GoACallout type="success">You've completed all steps</GoACallout>
        );
      }
      if (foundSteps.filter((step) => step.completed === false).length > 0) {
        return (
          <GoACallout type="information" heading="Status: Report in progress">
            You've completed{' '}
            {foundSteps.filter((step) => step.completed === true).length} of{' '}
            {foundSteps.length} steps
          </GoACallout>
        );
      }
    }

    return <div></div>;
  };

  return getStatusCallout(collision.metadata?.formState);
};

export const PassengerCopyGenerator = ({
  order: order,
  passenger,
  stateConfiguration,
}: {
  order: number;
  passenger: Passenger;
  stateConfiguration: ConfigurationState;
}) => {
  const [locationSafetyString, setLocationSafetyString] = useState<string>('');
  const [passengerNameString, setPassengerNameString] = useState<string>('');
  const [passengerAddressString, setPassengerAddressString] =
    useState<string>('');
  const [passengerPhoneNumberString, setPassengerPhoneNumberString] =
    useState<string>('');

  useEffect(() => {
    setLocationSafetyString(
      [
        stateConfiguration.configuration.positionInVehicle.find((x) => {
          return x.id == passenger.positionInVehicle;
        })?.reviewPageLabel,
        stateConfiguration.configuration.safetyEquipment.find((x) => {
          return x.id == passenger.safetyEquipmentUsed;
        })?.reviewPageLabel,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(', '),
    );

    setPassengerNameString(
      [
        [passenger.firstName, passenger.lastName].join(' '),
        stateConfiguration.configuration.genders.find(
          (x) => x.id.toString() == passenger.gender,
        )?.value,
        passenger.dateOfBirth,
      ]
        .filter(
          (value) => value !== undefined && value !== null && value !== '',
        )
        .join(', '),
    );

    setPassengerAddressString(
      [
        passenger.address.address,
        passenger.address.city,
        passenger.address.province,
        passenger.address.postalCode,
        passenger.address.country,
      ]
        .filter(
          (value) => value !== undefined && value !== null && value !== '',
        )
        .join(', '),
    );

    setPassengerPhoneNumberString(
      [
        passenger.homePhoneNumber ? `Home: ${passenger.homePhoneNumber}` : '',
        passenger.workPhoneNumber ? `Work: ${passenger.workPhoneNumber}` : '',
      ]
        .filter((value) => value)
        .join(', '),
    );
  }, []);
  return (
    <div>
      <strong>Passenger {order} information:</strong>
      <div> {locationSafetyString}</div>
      <div>{passengerNameString}</div>
      <div>{passengerAddressString}</div>
      <div>{passengerPhoneNumberString}</div>
    </div>
  );
};

export const PassengerInformationCopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [passengerCountString, setPassengerCountString] = useState<string>('');

  useEffect(() => {
    if (
      collision.vehicles &&
      collision.vehicles![0] &&
      collision.vehicles![0].numberOfPassengers !== undefined &&
      collision.vehicles![0].numberOfPassengers !== null
    )
      setPassengerCountString(
        collision.vehicles![0].numberOfPassengers!.toString() ?? '',
      );
  }, []);

  return (
    <div className={styles.reviewcopy}>
      {passengerCountString != ''
        ? `How many passengers were in your vehicle? ${passengerCountString}`
        : ''}

      <div>
        {collision.vehicles && collision.vehicles![0].passengers
          ? collision.vehicles![0].passengers!.map((passenger, index) => {
            return (
              <div key={passenger.id}>
                <PassengerCopyGenerator
                  order={index + 1}
                  passenger={passenger}
                  stateConfiguration={stateConfiguration}
                />
              </div>
            );
          })
          : ''}
      </div>
    </div>
  );
};

export const DriverAndVehicleCopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [driverString, setDriverString] = useState<string>('');
  const [driversLicenceString, setDriversLicenceString] = useState<string>('');
  const [addressString, setAddressString] = useState<string>('');
  const [phoneNumberString, setPhoneNumberString] = useState<string>('');
  const [
    seatLocationAndSafetyEquipmentString,
    setSeatLocationAndSafetyEquipmentString,
  ] = useState<string>('');
  const [vehicleInfoString, setVehicleInfoString] = useState<string>('');
  const [areYouOwnerString, setAreYouOwnerString] = useState<string>('');
  const [estimateObtainedString, setEstimateObtainedString] =
    useState<string>('');
  const [estimateString, setEstimateString] = useState<string>('');
  const [insuranceString, setInsuranceString] = useState<string>('');
  const [ownerInfoString, setOwnerInfoString] = useState<string>('');
  const [ownerPhoneString, setOwnerPhoneString] = useState<string>('');

  useEffect(() => {
    if (
      collision.vehicles &&
      collision.vehicles[0] &&
      collision.vehicles![0].driver
    ) {
      setDriverString(
        [
          [
            collision.vehicles![0].driver!.firstName,
            collision.vehicles![0].driver!.middleName,
            collision.vehicles![0].driver!.lastName,
          ].join(' '),
          stateConfiguration.configuration.genders.find((x) => {
            return x.id.toString() == collision.vehicles![0].driver!.gender;
          })?.value,
          moment(collision.vehicles![0].driver!.dateOfBirth).format(
            'YYYY-MM-DD',
          ),
        ]
          .filter(
            (value) => value !== undefined && value !== null && value !== '',
          )
          .join(', '),
      );

      setDriversLicenceString(
        collision.vehicles![0].driver!.licenceNumber ?? '',
      );

      setAddressString(
        [
          collision.vehicles![0].driver!.address.address,
          collision.vehicles![0].driver!.address.city,
          collision.vehicles![0].driver!.address.province,
          collision.vehicles![0].driver!.address.postalCode,
          collision.vehicles![0].driver!.address.country,
        ]
          .filter(
            (value) => value !== undefined && value !== null && value !== '',
          )
          .join(', '),
      );

      setPhoneNumberString(
        [
          collision.vehicles![0].driver!.homePhoneNumber
            ? `Home: ${collision.vehicles![0].driver!.homePhoneNumber}`
            : '',
          collision.vehicles![0].driver!.workPhoneNumber
            ? `Work: ${collision.vehicles![0].driver!.workPhoneNumber}`
            : '',
        ]
          .filter((value) => value)
          .join(', '),
      );

      if (!isHitAndRun(collision.collisionType!, stateConfiguration)) {
        setSeatLocationAndSafetyEquipmentString(
          stateConfiguration.configuration.safetyEquipment.find((x) => {
            return x.id == collision.vehicles![0].driver!.safetyEquipmentUsed;
          })?.reviewPageLabel!,
        );
      }

      setVehicleInfoString(
        [
          collision.vehicles![0].licencePlateNumber,
          'AB',
          collision.vehicles![0].vinNumber,
          collision.vehicles![0].year,
          collision.vehicles![0].make,
          (collision.vehicles![0].doesVehicleModelMatchWithMOVEs === false &&
            collision.vehicles![0].modelNameFromMOVEs != null &&
            collision.vehicles![0].modelNameFromMOVEs !== '')
            ? collision.vehicles![0].modelNameFromMOVEs
            : collision.vehicles![0].model,
          stateConfiguration.configuration.objectIdentification.find((x) => {
            return x.id.toString() == collision.vehicles![0].type!;
          })?.reviewPageLabel!,
          stateConfiguration.configuration.vehicleColours.find((x) => {
            return x.id.toString() == collision.vehicles![0].color;
          })?.value,
        ]
          .filter(
            (value) => value !== undefined && value !== null && value !== '',
          )
          .join(', '),
      );

      setAreYouOwnerString(collision.vehicles![0].isOwner ? 'Yes' : 'No');
      if (collision.vehicles![0].owner) {
        setOwnerInfoString(
          [
            [
              collision.vehicles![0].owner!.firstName,
              collision.vehicles![0].owner!.middleName,
              collision.vehicles![0].owner!.lastName,
            ].join(' '),
            collision.vehicles![0].owner!.address.address,
            collision.vehicles![0].owner!.address.city,
            collision.vehicles![0].owner!.address.province,
            collision.vehicles![0].owner!.address.postalCode,
            collision.vehicles![0].owner!.address.country,
          ]
            .filter(
              (value) => value !== undefined && value !== null && value !== '',
            )
            .join(', '),
        );

        setOwnerPhoneString(
          [
            collision.vehicles![0].owner!.homePhoneNumber
              ? `Home: ${collision.vehicles![0].owner!.homePhoneNumber}`
              : '',
            collision.vehicles![0].owner!.workPhoneNumber
              ? `Work: ${collision.vehicles![0].owner!.workPhoneNumber}`
              : '',
          ]
            .filter((value) => value)
            .join(', '),
        );
      }
      setEstimateObtainedString(
        collision.vehicles![0].hasObtainedDamageEstimate ? 'Yes' : 'No',
      );
      setEstimateString(
        collision.vehicles![0].hasObtainedDamageEstimate &&
          collision.vehicles![0].damageEstimate != null &&
          collision.vehicles![0].damageEstimate != undefined
          ? 'Estimate damage: $' +
          collision.vehicles![0].damageEstimate.toString()
          : '',
      );

      setInsuranceString(
        [
          collision.vehicles![0].insuredBy?.companyName,
          collision.vehicles![0].insuredBy?.insurancePolicyNumber,
          collision.vehicles![0].insuredBy?.insurancePolicyExpirationDate,
        ]
          .filter(
            (value) => value !== undefined && value !== null && value !== '',
          )
          .join(', '),
      );
    }
  }, []);

  return (
    <div className={styles.reviewcopy}>
      <div style={{ display: displaySection(driverString != '') }}>
        <strong>Personal information:</strong>
        <div>{driverString}</div>
      </div>
      <div style={{ display: displaySection(driversLicenceString != '') }}>
        <div>Driver's licence number: {driversLicenceString}</div>
      </div>
      <div style={{ display: displaySection(addressString != '') }}>
        <strong>Contact information:</strong>
        <div>{addressString}</div>
      </div>
      <div style={{ display: displaySection(phoneNumberString != '') }}>
        <div>{phoneNumberString}</div>
      </div>
      <div
        style={{
          display: displaySection(seatLocationAndSafetyEquipmentString != ''),
        }}
      >
        <strong>Safety information:</strong>
        <div>{seatLocationAndSafetyEquipmentString}</div>
      </div>
      <div style={{ display: displaySection(vehicleInfoString != '') }}>
        <strong>Vehicle information:</strong>
        <div>{vehicleInfoString}</div>
      </div>
      <div style={{ display: displaySection(areYouOwnerString != '') }}>
        <strong>Are you the owner of this vehicle:</strong> {areYouOwnerString}
        <div style={{ display: displaySection(areYouOwnerString == 'No') }}>
          <strong>Owner information:</strong>
          <div>{ownerInfoString}</div>
          <div>{ownerPhoneString}</div>
        </div>
      </div>
      <div>
        <div
          style={{
            display: displaySection(estimateObtainedString != ''),
          }}
        >
          Have you obtained an estimate for the damage to your vehicle:{' '}
          {estimateObtainedString}
        </div>
        <div style={{ display: displaySection(estimateString != '') }}>
          {estimateString}
        </div>
        <div
          style={{
            display: displaySection(insuranceString != ''),
          }}
        >
          <strong> Insurance information:</strong>
          <div>{insuranceString}</div>
        </div>
      </div>
    </div>
  );
};

export const ContributingFactorsCopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [whichWeatherConditionString, setWhichWeatherConditionString] =
    useState<string>('');
  const [whichLightConditionAString, setWhichLightConditionAString] =
    useState<string>('');

  const [whichLightConditionBString, setWhichLightConditionBString] =
    useState<string>('');

  const [whichRoadConditionString, setWhichRoadConditionString] =
    useState<string>('');

  const [whichRoadSurfaceConditionString, setWhichRoadSurfaceConditionString] =
    useState<string>('');

  const [whichTrafficControlDeviceString, setWhichTrafficControlDeviceString] =
    useState<string>('');

  const [
    trafficControlDeviceConditionString,
    setTrafficControlDeviceConditionString,
  ] = useState<string>('');

  const [isVehicleConditionAFactorString, setIsVehicleConditionAFactorString] =
    useState<string>('');

  const [whichVehicleConditionString, setWhichVehicleConditionString] =
    useState<string>('');

  const [noneOfTheAboveString, setNoneOfTheAboveString] = useState<string>('');

  useEffect(() => {
    if (collision.vehicles && collision.vehicles[0]) {
      setWhichWeatherConditionString(
        collision.vehicles![0].environmentalCondition
          ? stateConfiguration.configuration.weatherCondition.find((x) => {
            return x.id == collision.vehicles![0].environmentalCondition;
          })?.reviewPageLabel!
          : 'Not applicable',
      );

      setWhichLightConditionAString(
        collision.vehicles![0].lightConditionA
          ? stateConfiguration.configuration.lightConditionA.find((x) => {
            return x.id == collision.vehicles![0].lightConditionA;
          })?.reviewPageLabel!
          : 'Not applicable',
      );

      setWhichLightConditionBString(
        collision.vehicles![0].lightConditionB
          ? stateConfiguration.configuration.lightConditionB.find((x) => {
            return x.id == collision.vehicles![0].lightConditionB;
          })?.reviewPageLabel!
          : 'Not applicable',
      );

      setWhichRoadConditionString(
        collision.vehicles![0].contributingRoadCondition
          ? stateConfiguration.configuration.contributingRoadCondition.find(
            (x) => {
              return x.id == collision.vehicles![0].contributingRoadCondition;
            },
          )?.reviewPageLabel!
          : 'Not applicable',
      );

      setWhichRoadSurfaceConditionString(
        collision.vehicles![0].surfaceCondition
          ? stateConfiguration.configuration.surfaceCondition.find((x) => {
            return x.id == collision.vehicles![0].surfaceCondition;
          })?.reviewPageLabel!
          : 'Not applicable',
      );

      setWhichTrafficControlDeviceString(
        collision.vehicles![0].trafficControlDevice
          ? stateConfiguration.configuration.trafficControlDevice.find((x) => {
            return x.id == collision.vehicles![0].trafficControlDevice;
          })?.reviewPageLabel!
          : 'Not applicable',
      );

      setTrafficControlDeviceConditionString(
        collision.vehicles![0].trafficControlCondition
          ? stateConfiguration.configuration.trafficControlCondition.find(
            (x) => {
              return x.id == collision.vehicles![0].trafficControlCondition;
            },
          )?.reviewPageLabel!
          : 'Not applicable',
      );

      setIsVehicleConditionAFactorString(
        getYesNoOrUnknownString(
          collision.vehicles![0].isVehicleConditionAFactor,
        ) ?? 'Not applicable',
      );

      setWhichVehicleConditionString(
        collision.vehicles![0].vehicleCondition
          ? stateConfiguration.configuration.vehicleCondition.find((x) => {
            return x.id == collision.vehicles![0].vehicleCondition;
          })?.reviewPageLabel!
          : 'Not applicable',
      );

      setNoneOfTheAboveString(
        collision.vehicles![0].isNoContributingFactors
          ? 'Yes'
          : 'Not applicable',
      );
    }
  }, [collision]);

  return (
    <div className={styles.reviewcopy}>
      <div
        style={{
          display: displaySection(
            whichWeatherConditionString !== '' &&
            whichWeatherConditionString != 'Not applicable',
          ),
        }}
      >
        <strong>Weather conditions:</strong>

        <div>
          Which weather condition was a contributing factor in your collision:{' '}
          {whichWeatherConditionString}
        </div>
      </div>
      <div
        style={{
          display: displaySection(
            whichLightConditionAString != '' &&
            whichLightConditionAString != 'Not applicable',
          ),
        }}
      >
        <strong>Light conditions:</strong>

        <div>
          Which light condition was a contributing factor in your collision:{' '}
          {whichLightConditionAString}
        </div>
        <div>Were there any streetlights: {whichLightConditionBString}</div>
      </div>
      <div
        style={{
          display: displaySection(
            whichRoadConditionString != '' &&
            whichRoadConditionString != 'Not applicable',
          ),
        }}
      >
        <strong>Road and surface conditions:</strong>
        <div>
          Which road condition was a contributing factor in your collision:{' '}
          {whichRoadConditionString}
        </div>
      </div>
      <div
        style={{
          display: displaySection(
            whichRoadSurfaceConditionString != '' &&
            whichRoadSurfaceConditionString != 'Not applicable',
          ),
        }}
      >
        Which road surface condition was a contributing factor in your
        collision: {whichRoadSurfaceConditionString}
      </div>
      <div
        style={{
          display: displaySection(
            whichTrafficControlDeviceString != '' &&
            whichTrafficControlDeviceString != 'Not applicable',
          ),
        }}
      >
        <strong>Traffic control devices:</strong>

        <div>
          <div>
            Which traffic control device was the contributing factor in your
            collision: {whichTrafficControlDeviceString}
          </div>
          <div>
            Indicate the condition of the traffic device selected:{' '}
            {trafficControlDeviceConditionString}
          </div>
        </div>
      </div>

      <div
        style={{
          display: displaySection(
            whichVehicleConditionString != '' &&
            whichVehicleConditionString != 'Not applicable',
          ),
        }}
      >
        <strong>Vehicle conditions:</strong>

        <div>
          Which vehicle condition was the contributing factor in your collision:{' '}
          {whichVehicleConditionString}
        </div>
      </div>

      <div
        style={{
          display: displaySection(
            noneOfTheAboveString != '' &&
            noneOfTheAboveString != 'Not applicable',
          ),
        }}
      >
        <strong>None of the above:</strong>
        <div>{noneOfTheAboveString}</div>
      </div>
    </div>
  );
};

export const DamageCopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [firstPointOfImpactString, setFirstPointOfImpactString] =
    useState<string>('');
  const [damageAreasString, setDamageAreaString] = useState<string>('');
  const [isAttachment, setIsAttachment] = useState<boolean>(false);
  const [attachmentTypeString, setAttachmentTypeString] = useState<string>('');
  const [loadDetailsString, setLoadDetailsString] = useState<string>('');
  const [spillLoadString, setSpillLoadString] = useState<string>('');
  const [additionalDamageDetailsString, setAdditionalDamageDetailsString] =
    useState<string>('');

  useEffect(() => {
    if (collision.vehicles) {
      setFirstPointOfImpactString(
        stateConfiguration.configuration.pointOfImpact.find(
          (x) =>
            x.id.toString() ===
            collision
              .vehicles![0].damage!.damageSelection!.find(
                (x) => x.isInitialPointOfImpact == true,
              )!
              .pointOfImpactId.toString(),
        )?.reviewPageLabel!,
      );

      if (collision.vehicles) {
        if (collision.vehicles![0].damage!.damageSelection!.length > 0) {
          const additionDamageStrings: string[] = [];
          collision.vehicles![0].damage!.damageSelection!.map((poi) => {
            const foundPointOfImpact =
              stateConfiguration.configuration.pointOfImpact.find((config) => {
                return config.id.toString() == poi.pointOfImpactId.toString();
              });

            if (foundPointOfImpact) {
              additionDamageStrings.push(foundPointOfImpact.reviewPageLabel!);
            }
          });
          setDamageAreaString(additionDamageStrings.join(', '));
        }

        collision.vehicles![0].damage!.damageSelection!.forEach((poi) => {
          if (
            getConfigurationByDescription(
              stateConfiguration.configuration.pointOfImpact,
              InitialPointOfImpact.ATTACHMENT,
            )?.id == parseInt(poi.pointOfImpactId)
          ) {
            setIsAttachment(true);
            setAttachmentTypeString(
              stateConfiguration.configuration.attachments.find(
                (x) =>
                  x.id === parseInt(collision.vehicles![0].damage!.attachment!),
              )?.reviewPageLabel!,
            );

            setLoadDetailsString(
              stateConfiguration.configuration.loadDetails.find((x) => {
                return x.id == collision.vehicles![0].damage!.loadDetail;
              })?.reviewPageLabel!,
            );

            setSpillLoadString(
              stateConfiguration.configuration.loadSpillage.find((x) => {
                return x.id == collision.vehicles![0].damage!.loadSpillage;
              })?.reviewPageLabel!,
            );
          }
        });

        if (collision.vehicles![0].damage!.additionalDamageDetails != null) {
          setAdditionalDamageDetailsString(
            collision.vehicles![0].damage!.additionalDamageDetails!,
          );
        }
      }
    }
  }, []);

  return (
    <div className={styles.reviewcopy}>
      <div>Indicate vehicle damage areas: {damageAreasString}</div>
      <div style={{ display: displaySection(firstPointOfImpactString != '') }}>
        Indicate the first point of impact: {firstPointOfImpactString}
      </div>
      <div style={{ display: displaySection(isAttachment) }}>
        <div>Indicate the type of attachment: {attachmentTypeString}</div>
        <div>
          Was your {attachmentTypeString} carrying a load: {loadDetailsString}
        </div>
        <div>
          Did you lose and/or spill any items from your load: {spillLoadString}
        </div>
      </div>
      <div
        style={{ display: displaySection(additionalDamageDetailsString != '') }}
      >
        Additional damage details: {additionalDamageDetailsString}
      </div>
    </div>
  );
};

export const DirectionOfTravelCopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [directionOfTravelString, setDirectionOfTravelString] =
    useState<string>('');

  const [primaryRoadTypesString, setPrimaryRoadTypesString] =
    useState<string>('');

  const [specialRoadTypesString, setSpecialRoadTypesString] =
    useState<string>('');

  const [collisionLocationString, setCollisionLocationString] =
    useState<string>('');

  const [roadAlignmentAString, setRoadAlignmentAString] = useState<string>('');
  const [roadAlignmentBString, setRoadAlignmentBString] = useState<string>('');
  const [travelingLaneString, setTravelLaneString] = useState<string>('');

  useEffect(() => {
    if (collision.directionOfTravel) {
      setDirectionOfTravelString(
        stateConfiguration.configuration.directionOfTravel.find(
          (x) => x.id === collision.directionOfTravel,
        )?.reviewPageLabel!,
      );
    }

    if (collision.roadClass) {
      setPrimaryRoadTypesString(
        stateConfiguration.configuration.roadClasses.find((x) => {
          return x.id === collision.roadClass;
        })?.reviewPageLabel!,
      );
    }

    if (collision.travelingLane) {
      setTravelLaneString(
        stateConfiguration.configuration.travelingLane.find((x) => {
          return x.id === collision.travelingLane;
        })?.reviewPageLabel!,
      );
    }

    if (collision.specialFacility) {
      setSpecialRoadTypesString(
        stateConfiguration.configuration.specialFacilities.find((x) => {
          return x.id === collision.specialFacility;
        })?.reviewPageLabel!,
      );
    }

    if (collision.collisionLocation) {
      setCollisionLocationString(
        stateConfiguration.configuration.collisionLocation.find((x) => {
          return x.id === collision.collisionLocation;
        })?.reviewPageLabel!,
      );
    }

    if (collision.roadAlignmentA) {
      setRoadAlignmentAString(
        stateConfiguration.configuration.roadAlignmentA.find((x) => {
          return x.id === collision.roadAlignmentA;
        })?.reviewPageLabel!,
      );
    }

    if (collision.roadAlignmentB) {
      setRoadAlignmentBString(
        stateConfiguration.configuration.roadAlignmentB.find((x) => {
          return x.id === collision.roadAlignmentB;
        })?.reviewPageLabel!,
      );
    }
  }, []);

  return (
    <div className={styles.reviewcopy}>
      <div className={styles.reviewcopy}>
        <div style={{ display: displaySection(directionOfTravelString != '') }}>
          <strong>Direction of travel: </strong>
          {directionOfTravelString}
        </div>
        <div style={{ display: displaySection(primaryRoadTypesString != '') }}>
          <strong>Road types:</strong>
          <br />
          <div>
            <div>Primary road types: {primaryRoadTypesString}</div>
          </div>
        </div>
        <div style={{ display: displaySection(travelingLaneString != '') }}>
          <div>
            <div>
              Choose a travelling lane from the median: {travelingLaneString}
            </div>
          </div>
        </div>
        <div style={{ display: displaySection(specialRoadTypesString != '') }}>
          <div>
            <div>Secondary road types: {specialRoadTypesString}</div>
          </div>
        </div>
        <div>
          <div
            style={{ display: displaySection(collisionLocationString != '') }}
          >
            <strong>Intersections and railroad crossings:</strong>
          </div>
          <div>{collisionLocationString}</div>
        </div>
        <div style={{ display: displaySection(roadAlignmentAString != '') }}>
          <div>
            <strong>Road characteristics:</strong>
          </div>
          <div>
            What type of road surface were you travelling on:{' '}
            {roadAlignmentAString}
          </div>
          <div>Was the road straight or curved: {roadAlignmentBString}</div>
        </div>
      </div>
    </div>
  );
};

export const DateAndTimeopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [collisionDateString, setCollisionDateString] = useState<string>('');

  useEffect(() => {
    if (collision.dateOfCollision && collision.timeOfCollision) {
      setCollisionDateString(
        moment(collision.dateOfCollision).format('MMMM DD, YYYY') +
        ' at ' +
        collision.timeOfCollision,
      );
    }
  }, [collision.dateOfCollision, collision.timeOfCollision]);

  return <div className={styles.reviewcopy}>{collisionDateString}</div>;
};

export interface LocationCopyGeneratorProps extends CopyGeneratorProps {
  showLocationText: boolean;
  onLoaded?: () => void;
}

export const LocationCopyGenerator = ({
  collision,
  printView = false,
  showLocationText = false,
  onLoaded = () => { },
}: LocationCopyGeneratorProps) => {
  const apiKey = REACT_APP_GOOGLE_API_KEY;

  const map = collision.location ? (
    <APIProvider apiKey={apiKey} onLoad={() => { }}>
      <ReviewMapComponent
        lat={collision.location?.latitude ?? 0}
        lng={collision.location?.longitude ?? 0}
        zoom={collision.location?.zoom ?? 15}
        apiKey={apiKey}
        onLoaded={onLoaded}
      ></ReviewMapComponent>
    </APIProvider>
  ) : (
    ''
  );

  const mapView = !printView ? (
    <GoAAccordion heading="View Map">
      <div
        style={{
          width: '100%',
          display: 'flex',
        }}
      >
        {map}
      </div>
    </GoAAccordion>
  ) : (
    map
  );

  return collision.location ? (
    <div className={styles.reviewcopy}>
      <GoASpacer vSpacing={'2xs'} />
      <div>{collision.location!.locationString ?? ''}</div>
      <div>{collision.location!.additionalLocationDetails ?? ''}</div>
      <GoASpacer vSpacing={'s'} />
      {mapView}
    </div>
  ) : (
    ''
  );
};

export const CollisiontTypeCopyGenerator = ({
  collision,
  stateConfiguration,
}: CopyGeneratorProps) => {
  const [collisionTypeString, setCollisionTypeString] = useState<string>('');
  const [collisionTypeAnswerString, setCollisionTypeAnswerString] =
    useState<string>('');

  useEffect(() => {
    if (collision.collisionType) {
      const collisionTypeFound =
        stateConfiguration.configuration.collisionType.find(
          (x) => x.id === collision.collisionType,
        );

      if (collisionTypeFound) {
        setCollisionTypeString(collisionTypeFound.reviewPageLabel!);
      }

      if (
        getConfigurationByDescription(
          stateConfiguration.configuration.collisionType,
          CollisionTypes.ANIMAL,
        )?.id == collision.collisionType
      ) {
        const animalType = stateConfiguration.configuration.animalType.find(
          (x) => x.id == collision.animalType,
        );
        const animalTypeOther =
          stateConfiguration.configuration.otherAnimalType.find(
            (x) => x.id == collision.animalType,
          );

        setCollisionTypeAnswerString(
          animalType
            ? animalType!.reviewPageLabel!
            : animalTypeOther
              ? animalTypeOther.reviewPageLabel!
              : '',
        );
      }

      if (
        getConfigurationByDescription(
          stateConfiguration.configuration.collisionType,
          CollisionTypes.LEFT_SIDE_ROAD,
        )?.id == collision.collisionType ||
        getConfigurationByDescription(
          stateConfiguration.configuration.collisionType,
          CollisionTypes.RIGHT_SIDE_ROAD,
        )?.id == collision.collisionType
      ) {
        if (
          collision.objectIdentification !=
          getConfigurationByDescription(
            stateConfiguration.configuration.objectIdentification,
            ObjectIdentification.FIXED_OBJECT,
          )?.id
        ) {
          setCollisionTypeAnswerString('');
        } else if (
          collision.objectIdentification ==
          getConfigurationByDescription(
            stateConfiguration.configuration.objectIdentification,
            ObjectIdentification.FIXED_OBJECT,
          )?.id
        ) {
          setCollisionTypeAnswerString(
            'Vehicle ran off road and hit an object',
          );
        }
      }

      if (
        getConfigurationByDescription(
          stateConfiguration.configuration.collisionType,
          CollisionTypes.OBJECT_ON_ROADWAY,
        )?.id == collision.collisionType &&
        getConfigurationByDescription(
          stateConfiguration.configuration.objectIdentification,
          ObjectIdentification.FIXED_OBJECT,
        )!.id == collision.objectIdentification
      ) {
        const fixedObject = getConfigurationByDescription(
          stateConfiguration.configuration.objectIdentification,
          ObjectIdentification.FIXED_OBJECT,
        );
        setCollisionTypeAnswerString(
          fixedObject ? fixedObject.reviewPageLabel! : '',
        );
      } else if (
        getConfigurationByDescription(
          stateConfiguration.configuration.collisionType,
          CollisionTypes.OBJECT_ON_ROADWAY,
        )?.id == collision.collisionType &&
        getConfigurationByDescription(
          stateConfiguration.configuration.objectIdentification,
          ObjectIdentification.FIXED_OBJECT,
        )!.id != collision.objectIdentification
      ) {
        setCollisionTypeAnswerString('My vehicle hit a non-fixed object');
      }
    }
  }, [collision.collisionType]);
  return (
    <div className={styles.reviewcopy}>
      {collisionTypeString
        ? 'What type of collision were you in: ' + collisionTypeString
        : ''}
      {collisionTypeAnswerString ? ': ' : ''}
      {collisionTypeAnswerString}
    </div>
  );
};

export interface MediaCopyGenerater {
  mediaState: MediaState;
  onPhotosReady?: () => void;
}
export const MediaCopyGenerater = ({
  mediaState,
  onPhotosReady,
}: MediaCopyGenerater) => {
  const csrService = useCsrService();
  const isDesktop = useIsDesktop();

  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (
      mediaState.images &&
      mediaState.images.length > 0 &&
      mediaState.images.length > 0 &&
      mediaState.images.length == images.length
    ) {
      if (onPhotosReady) onPhotosReady();
    }
  }, [images]);

  const handleImagesLoaded = (imageName: string) => {
    setImages([...images, imageName]);
  };

  return (
    <div>
      <div
        style={
          isDesktop
            ? {}
            : {
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }
        }
      >
        <div className={styles.gridcontainer}>
          {mediaState.images.map((image, index) => {
            if (image.length > 0) {
              return (
                <div key={image}>
                  <ImageCard
                    imagePromise={loadImage}
                    deleteFile={() => { }}
                    key={index}
                    showDeleteFile={false}
                    fileName={image}
                    axiosInstance={csrService}
                    height={isDesktop ? 110 : 100}
                    width={isDesktop ? 120 : 100}
                    showMedaLinks={false}
                    showBorder={false}
                    onLoadingComplete={() => {
                      handleImagesLoaded(image);
                    }}
                  />
                </div>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};
